// @flow
import isEmptyObject from 'is-empty-object';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import {
  MdClose,
  MdKeyboardArrowDown,
  MdDirections,
  MdEdit,
  MdDelete,
  MdBlock,
} from 'react-icons/lib/md';
import * as reactRouter from 'react-router-redux';
import i18n from 'i18n-js';

import type { ROUTE_TYPE, ROUTE_STOP_TYPE } from '../../types/routes';
import type { MONITOR_TYPE } from '../../types/monitors';
import type { DRIVER_TYPE } from '../../types/drivers';
import Button from '../Button';
import { close, open } from '../../actions/closeable';
import OmnisearchLoadingResults from '../OmnisearchLoadingResults';
import CardHeader from '../CardHeader';
import CardContent, { LoadingCardContent } from '../CardContent';
import CardFooter from '../CardFooter';
import CardHeaderTitleContainer from '../CardHeaderTitleContainer';
import CardHeaderTitle from '../CardHeaderTitle';
import CardHeaderSubtitle from '../CardHeaderSubtitle';
import Popup from '../Popup';
import CreateRoutePopupContent from '../CreateRoutePopupContent';
import * as selectors from '../../reducers';
import * as routesActions from '../../actions/routes';
import * as tripActions from '../../actions/trips';
import RouteCardStop from '../RouteCardStop';
import User from '../User';
import PopMenu from '../PopMenu';

import styles from './generalRouteInfoCardContent.css';
import { Delete, ConfirmDeletion } from '../CommonTexts';
import ConfirmDeletionPopup from '../ConfirmDeletionPopup';
import PopupConfirmations from '../PopupConfirmations';
import { getUserImage } from '../../utils';


type GeneralRouteInfoCardContentPropTypes = {
  route?: ROUTE_TYPE,
  routeStop?: ROUTE_STOP_TYPE,
  firstStop?: ROUTE_STOP_TYPE,
  monitor?: MONITOR_TYPE,
  driver?: DRIVER_TYPE,
  onClick?: Function,
  push: Function,
  onDeleteClicked: Function,
  isDeleting: boolean,
  finishTrip: Function,
  tripId: ID_TYPE,
};

const RouteOptionsDummy = ({ onEdit, onDelete }: { onEdit: Function, onDelete: Function }) => (
  <PopMenu
    id="routeOptionsPopover"
    vertical="below"
    horizontal="below"
    tipPosition="middle"
    isRound
    options={[
      {
        Icon: MdEdit,
        title: i18n.t('buttons.edit'),
        onClick: onEdit,
      },
      {
        Icon: MdDelete,
        title: Delete(),
        tint: 'error',
        onClick: onDelete,
      },
    ]}
  >
    <Button
      size="medium"
      Icon={MdKeyboardArrowDown}
      iconPosition="right"
      tint="mediumGray"
      clearMargins
      isTransparent
      isOutlined
    />
  </PopMenu>
);

const RouteOptions = connect(
  undefined,
  dispatch => ({
    onEdit() {
      dispatch(open('editRoutePopup'));
    },
    onDelete() {
      dispatch(open('confirmDeleteRoute'));
    },
  }),
)(RouteOptionsDummy);

const DummyGeneralRouteInfoCardContent = ({
  route,
  routeStop,
  monitor,
  firstStop,
  driver,
  onClick,
  onDeleteClicked,
  isDeleting,
  push,
  finishTrip,
  tripId,
  openConfirmFinishTripPopup,
  closeConfirmFinishTripPopup,
}: GeneralRouteInfoCardContentPropTypes) => (
  typeof route !== 'undefined' && !isEmptyObject(route)
    ? (
      <Fragment>
        <CardHeader className={styles.cardHeader}>
          <CardHeaderTitleContainer>
            <CardHeaderTitle
              title={route.name}
              className={styles.titleCustomStyle}
              titleRightButton={(
                <div className={styles.routeOptions}>
                  <Popup
                    id="confirmDeleteRoute"
                    theme="main"
                    transitionType="slideDown"
                    transitionMs={600}
                  >
                    <ConfirmDeletionPopup
                      onConfirm={() => onDeleteClicked()}
                      Icon={MdDelete}
                      descriptionText={ConfirmDeletion(route.name)}
                      loading={isDeleting}
                    />
                  </Popup>
                  <RouteOptions />
                  <Popup
                    id="editRoutePopup"
                    theme="main"
                    transitionType="slideDown"
                    transitionMs={600}
                  >
                    <CreateRoutePopupContent routeId={route.id} />
                  </Popup>
                </div>
              )}
            />
            <CardHeaderSubtitle subtitle={route.route_type} />
          </CardHeaderTitleContainer>
          <Button
            Icon={MdClose}
            clearMargins
            isTransparent
            className={styles.routeInfoCloseButton}
            size="small"
            onClick={() => push('/admin/map')}
          />
        </CardHeader>
        {
          typeof routeStop !== 'undefined'
          && (
            <Fragment>
              <CardContent
                tint="mediumGray"
              >
                <RouteCardStop
                  {...routeStop}
                  firstStop={firstStop}
                  isDestiny
                />
              </CardContent>
              <CardContent
                tint="mediumGray"
              >
                <Button
                  isFullWidth
                  tint="secondary"
                  title={`${i18n.t('common.finishTrip')}`}
                  Icon={MdBlock}
                  clearMargins
                  onClick={() => openConfirmFinishTripPopup()}
                />
                <PopupConfirmations
                  id="CONFIRM_FINISH_TRIP"
                  title={i18n.t('common.finishTripQuestionMessage')}
                  size="small"
                  onCancel={() => closeConfirmFinishTripPopup()}
                  onConfirm={() => {
                    finishTrip(tripId);
                    closeConfirmFinishTripPopup();
                  }}
                >
                  <p className={styles.popupConfirmBody}>
                    {i18n.t('common.finishTripWarningMessage')}
                  </p>
                </PopupConfirmations>
              </CardContent>
            </Fragment>
          )
        }
        {
          typeof monitor !== 'undefined'
            ? (
              <CardContent>
                <User
                  firstName={monitor.first_name}
                  lastName={monitor.last_name}
                  pictureURL={getUserImage(monitor.profile_picture)}
                  userRole="Monitor"
                />
              </CardContent>
            ) : (!isEmptyObject(route)
              ? (<User
                firstName={i18n.t('labels.no_assigned_monitor')}
                lastName=""
                pictureURL=""
                userRole="Monitor"
              />
              ) : <OmnisearchLoadingResults />
            )
        }
        {
          typeof driver !== 'undefined'
            ? (
              <CardContent>
                <User
                  firstName={driver.first_name}
                  lastName={driver.last_name}
                  pictureURL={getUserImage(driver.profile_picture)}
                  userRole="Conductor"
                />
              </CardContent>
            ) : <OmnisearchLoadingResults />
        }
        <CardFooter clearPaddings>
          <Button
            className={styles.buttonHeight}
            size="medium"
            title={i18n.t('buttons.exploreStops')}
            Icon={MdDirections}
            isBottom
            isFullWidth
            tint="primary"
            onClick={onClick}
          />
        </CardFooter>
      </Fragment>
    ) : (
      <Fragment>
        <CardHeader />
        <LoadingCardContent loaderTint="mediumGray" />
        <CardFooter />
      </Fragment>
    )
);

const GeneralRouteInfoCardContent = connect(
  (state, { id }) => {
    const route = selectors.getRoute(state, id);
    let monitorObj;
    let driverObj;
    if (typeof route !== 'undefined') {
      const { monitor, driver } = route;

      if (monitor && monitor.profiles) {
        const monitorId = monitor.profiles.filter(p => p.type === 'monitor')[0].id;
        monitorObj = selectors.getMonitor(state, monitorId);
      }

      if (driver && driver.profiles) {
        const driverId = driver.profiles.filter(p => p.type === 'driver')[0].id;
        driverObj = selectors.getDriver(state, driverId);
      }

      const activeTrips = selectors.getRouteActiveTrips(state, id);
      let nextStop;
      let firstStop;
      let tripId;
      if (activeTrips && activeTrips.length > 0) {
        tripId = activeTrips[0].id;
        nextStop = selectors.getNextTripStop(state, activeTrips[0].id);
        firstStop = selectors.getFirstTripStop(state, activeTrips[0].id);
      }
      return {
        route,
        firstStop,
        tripId,
        monitor: monitorObj,
        driver: driverObj,
        routeStop: nextStop,
        isDeleting: selectors.isFetchingRoutes(state),
      };
    }

    return {};
  },
  (dispatch, { id }) => ({
    onClick() {
      dispatch(routesActions.showStopsDetail(id));
    },
    // TODO: check this serious change, before passed anonymous function to dispatch??
    finishTrip: tripId => dispatch(tripActions.finishTripStarted(tripId)),
    open: lId => dispatch(open(lId)),
    push: (route: string) => dispatch(reactRouter.push(route)),
    onDeleteClicked(route) {
      dispatch(routesActions.deleteRouteStarted(route.id));
    },
    openConfirmFinishTripPopup() {
      dispatch(open('CONFIRM_FINISH_TRIP'));
    },
    closeConfirmFinishTripPopup() {
      dispatch(close('CONFIRM_FINISH_TRIP'));
    },
  }),
)(DummyGeneralRouteInfoCardContent);


export default GeneralRouteInfoCardContent;



// WEBPACK FOOTER //
// ./src/scripts/components/GeneralRouteInfoCardContent/index.jsx